import { useRouter } from 'next/navigation';
import { useHaveFeatureFlagsLoaded } from '@frameio/feature-flags';
import { useMeQuery } from '@frameio/graphql-client/graphql/shared/queries/generated/Me';
import { useActiveInboxAccount } from '@frameio/inbox/hooks/useActiveInboxAccount';
import { useActiveAccountId } from '@frameio/shared/contexts/ActiveEntitiesContext/ActiveAccountIdContext';
import { useDispatchAppState } from '@frameio/shared/contexts/AppStateContext/appStateSelectors';
import { useIsMatchingClientName } from '@frameio/shared/contexts/ClientNameContext';
import { getLink } from '@frameio/shared/routing/getLink';
import { setPendingSettingsScope } from '@frameio/shared/settings/workspaceSettingsNavigation';
import { Avatar, MenuItem, Text } from '@frameio/vapor';
import { IconCog } from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/UserAvatar';
import styles from './UserAvatar.module.css';

TEXT.DEFINE({
  /** Accessible label for opening the user's Personal Settings from the account menu */
  personalSettings: 'Personal Settings',
});

export const UserAvatar = () => {
  const { data } = useMeQuery();
  const user = data?.me;
  const userName = user?.name ?? '';
  const accountId = useActiveAccountId();
  const { inboxAccount, loading: inboxAccountsLoading } =
    useActiveInboxAccount();
  const router = useRouter();
  const dispatch = useDispatchAppState();
  const isWebClient = useIsMatchingClientName('web');
  const haveFeatureFlagsLoaded = useHaveFeatureFlagsLoaded();
  const href = inboxAccount?.id
    ? getLink({
        page: 'inbox',
        settingsSection: 'profile',
        accountId: inboxAccount.id,
      })
    : getLink({ page: 'settings', section: 'profile' });
  const isNavigationDisabled = Boolean(
    !haveFeatureFlagsLoaded ||
      inboxAccountsLoading ||
      (!inboxAccount?.id && !accountId)
  );

  const content = (
    <>
      <Avatar
        alt={userName}
        aria-hidden="true"
        className={styles.avatar}
        size={48}
        src={
          user?.__typename === 'AuthenticatedUser'
            ? user.profileImage?.image256 ?? undefined
            : undefined
        }
      />
      <div className={styles.details}>
        <Text
          enableTooltip={true}
          fontWeight="medium"
          truncate={true}
          variant="primary"
        >
          {userName}
        </Text>
        <Text
          color="tertiary"
          enableTooltip={true}
          truncate={true}
          variant="caption"
        >
          {user?.email}
        </Text>
      </div>
      {isWebClient && <IconCog className={styles.icon} />}
    </>
  );

  if (!isWebClient) {
    return <div className={styles.profileRow}>{content}</div>;
  }

  const handlePersonalSettingsPress = () => {
    if (accountId && !inboxAccount?.id) {
      setPendingSettingsScope({ accountId, type: 'personal' });
    }
    dispatch({ type: 'CLOSE_MOBILE_NAV' });
    router.push(href);
  };

  return (
    <MenuItem
      disabled={isNavigationDisabled}
      onPress={handlePersonalSettingsPress}
    >
      {({ bind }) => (
        <button
          {...bind}
          aria-label={TEXT.personalSettings}
          className={`${styles.profileRow} ${styles.profileButton}`}
          data-testid="primary-nav--personal-settings-button"
          disabled={isNavigationDisabled}
          role="menuitem"
          type="button"
        >
          {content}
        </button>
      )}
    </MenuItem>
  );
};
